import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Trash2, Plus, Loader2, ExternalLink, Pencil, Save, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { BRAND, BLOG_CATEGORIES, PROJECT_CATEGORIES } from "@/lib/content";
import { toast } from "sonner";

function Field({ label, ...props }) {
  return (
    <div>
      <label className="text-xs text-[#94A3B8] mb-1.5 block">{label}</label>
      <Input {...props} className="bg-white/5 border-white/15 h-11 text-white placeholder:text-[#64748B]" />
    </div>
  );
}

function ImageFields({ value, alt, caption, onChange, onMetaChange }) {
  const choose = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choose an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be 5 MB or smaller");
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(file);
  };
  return <div className="space-y-3 rounded-xl border border-white/10 p-4">
    <label className="text-xs text-[#94A3B8] block">Image file (up to 5 MB)</label>
    <Input type="file" accept="image/*" onChange={(e) => choose(e.target.files?.[0])} className="bg-white/5 border-white/15 text-white file:text-white" />
    <Field label="Or image URL" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://..." />
    {value && <img src={value} alt={alt || "Preview"} className="h-24 rounded object-cover" />}
    <Field label="Image alt text" value={alt || ""} onChange={(e) => onMetaChange("image_alt", e.target.value)} placeholder="Describe the image" />
    <Field label="Image caption" value={caption || ""} onChange={(e) => onMetaChange("image_caption", e.target.value)} placeholder="Optional caption" />
  </div>;
}

function InternalLinkField({ value, onChange }) {
  return <Field label="Internal link (optional)" value={value || ""} onChange={onChange} placeholder="/about or /projects/example" />;
}

function Section({ title, children }) {
  return <div className="glass rounded-2xl p-6 mb-6"><h3 className="font-display text-2xl mb-5">{title}</h3>{children}</div>;
}

function EditSection({ title, onSubmit, onCancel, children }) {
  return (
    <Section title={title}>
      <form onSubmit={onSubmit} className="space-y-4">
        {children}
        <div className="flex gap-3">
          <button className="inline-flex items-center gap-2 rounded-xl bg-primary-green px-5 h-11 text-white font-medium hover:bg-[#059669]"><Save size={16} /> Save Changes</button>
          <button type="button" onClick={onCancel} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 h-11 text-white hover:border-white/40"><X size={16} /> Cancel</button>
        </div>
      </form>
    </Section>
  );
}

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [f, setF] = useState({ title: "", category: "Scientific Vastu", excerpt: "", content: "", cover_image: "", image_alt: "", image_caption: "", internal_link: "" });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/blog").then((r) => setPosts(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!f.title || !f.excerpt || !f.content) return toast.error("Title, excerpt & content required");
    setBusy(true);
    try { await api.post("/blog", { ...f, published: true }); toast.success("Post created"); setF({ title: "", category: "Scientific Vastu", excerpt: "", content: "", cover_image: "", image_alt: "", image_caption: "", internal_link: "" }); load(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/blog/${id}`); toast.success("Deleted"); load(); };
  const update = async (e) => {
    e.preventDefault();
    if (!editing.title || !editing.excerpt || !editing.content) return toast.error("Title, excerpt & content required");
    try { await api.put(`/blog/${editing.id}`, editing); toast.success("Post updated"); setEditing(null); load(); } catch { toast.error("Update failed"); }
  };

  return (
    <>
      <Section title="Add Blog Post">
        <form onSubmit={create} className="space-y-4" data-testid="admin-blog-form">
          <Field label="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Post title" data-testid="blog-title-input" />
          <div>
            <label className="text-xs text-[#94A3B8] mb-1.5 block">Category</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full bg-white/5 border border-white/15 rounded-md h-11 px-3 text-white">
              {BLOG_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c} value={c} className="bg-[#0A1128]">{c}</option>)}
            </select>
          </div>
          <ImageFields value={f.cover_image} alt={f.image_alt} caption={f.image_caption} onChange={(cover_image) => setF({ ...f, cover_image })} onMetaChange={(key, value) => setF({ ...f, [key]: value })} />
          <InternalLinkField value={f.internal_link} onChange={(e) => setF({ ...f, internal_link: e.target.value })} />
          <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Excerpt</label><Textarea value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} rows={2} className="bg-white/5 border-white/15 text-white" /></div>
          <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Content</label><Textarea value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} rows={5} className="bg-white/5 border-white/15 text-white" /></div>
          <button disabled={busy} className="inline-flex items-center gap-2 rounded-xl bg-primary-green px-5 h-11 text-white font-medium hover:bg-[#059669]">{busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Publish Post</button>
        </form>
      </Section>
      {editing && <EditSection title="Edit Blog Post" onSubmit={update} onCancel={() => setEditing(null)}>
        <Field label="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
        <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Category</label><select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full bg-white/5 border border-white/15 rounded-md h-11 px-3 text-white">{BLOG_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c} value={c} className="bg-[#0A1128]">{c}</option>)}</select></div>
        <ImageFields value={editing.cover_image} alt={editing.image_alt} caption={editing.image_caption} onChange={(cover_image) => setEditing({ ...editing, cover_image })} onMetaChange={(key, value) => setEditing({ ...editing, [key]: value })} />
        <InternalLinkField value={editing.internal_link} onChange={(e) => setEditing({ ...editing, internal_link: e.target.value })} />
        <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Excerpt</label><Textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} className="bg-white/5 border-white/15 text-white" /></div>
        <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Content</label><Textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} rows={5} className="bg-white/5 border-white/15 text-white" /></div>
      </EditSection>}
      <Section title={`Published Posts (${posts.length})`}>
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center gap-4 rounded-xl border border-white/10 p-3">
              <img src={p.cover_image} alt="" className="h-12 w-16 rounded object-cover" />
              <div className="flex-1 min-w-0"><div className="text-white truncate">{p.title}</div><div className="text-xs text-gold">{p.category}</div></div>
              <button onClick={() => setEditing({ ...p })} className="text-gold hover:text-white p-2" aria-label={`Edit ${p.title}`}><Pencil size={16} /></button>
              <button onClick={() => del(p.id)} className="text-red-400 hover:text-red-300 p-2" data-testid={`delete-blog-${p.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

function ProjectManager() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", category: "Residential", subtype: "", location: "", image: "", image_alt: "", image_caption: "", internal_link: "", client_concern: "", what_i_found: "", solution_provided: "", outcome: "" });
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/projects").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    if (!f.title || !f.location) return toast.error("Title & location required");
    try { await api.post("/projects", f); toast.success("Project added"); setF({ title: "", category: "Residential", subtype: "", location: "", image: "", image_alt: "", image_caption: "", internal_link: "", client_concern: "", what_i_found: "", solution_provided: "", outcome: "" }); load(); } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/projects/${id}`); toast.success("Deleted"); load(); };
  const update = async (e) => {
    e.preventDefault();
    if (!editing.title || !editing.location) return toast.error("Title & location required");
    try { await api.put(`/projects/${editing.id}`, editing); toast.success("Project updated"); setEditing(null); load(); } catch { toast.error("Update failed"); }
  };
  return (
    <>
      <Section title="Add Project">
        <form onSubmit={create} className="space-y-4" data-testid="admin-project-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="project-title-input" />
            <Field label="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Category</label>
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full bg-white/5 border border-white/15 rounded-md h-11 px-3 text-white">
                {PROJECT_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c} value={c} className="bg-[#0A1128]">{c}</option>)}
              </select></div>
            <Field label="Subtype" value={f.subtype} onChange={(e) => setF({ ...f, subtype: e.target.value })} placeholder="e.g. Villas" />
          </div>
          <ImageFields value={f.image} alt={f.image_alt} caption={f.image_caption} onChange={(image) => setF({ ...f, image })} onMetaChange={(key, value) => setF({ ...f, [key]: value })} />
          <InternalLinkField value={f.internal_link} onChange={(e) => setF({ ...f, internal_link: e.target.value })} />
          <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Client Concern</label><Textarea value={f.client_concern} onChange={(e) => setF({ ...f, client_concern: e.target.value })} rows={2} className="bg-white/5 border-white/15 text-white" /></div>
          <div><label className="text-xs text-[#94A3B8] mb-1.5 block">What I Found</label><Textarea value={f.what_i_found} onChange={(e) => setF({ ...f, what_i_found: e.target.value })} rows={2} className="bg-white/5 border-white/15 text-white" /></div>
          <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Solution Provided</label><Textarea value={f.solution_provided} onChange={(e) => setF({ ...f, solution_provided: e.target.value })} rows={2} className="bg-white/5 border-white/15 text-white" /></div>
          <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Outcome</label><Textarea value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} rows={2} className="bg-white/5 border-white/15 text-white" /></div>
          <button className="inline-flex items-center gap-2 rounded-xl bg-primary-green px-5 h-11 text-white font-medium hover:bg-[#059669]"><Plus size={16} /> Add Project</button>
        </form>
      </Section>
      {editing && <EditSection title="Edit Project" onSubmit={update} onCancel={() => setEditing(null)}>
        <div className="grid grid-cols-2 gap-3"><Field label="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /><Field label="Location" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3"><div><label className="text-xs text-[#94A3B8] mb-1.5 block">Category</label><select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full bg-white/5 border border-white/15 rounded-md h-11 px-3 text-white">{PROJECT_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c} value={c} className="bg-[#0A1128]">{c}</option>)}</select></div><Field label="Subtype" value={editing.subtype || ""} onChange={(e) => setEditing({ ...editing, subtype: e.target.value })} /></div>
        <ImageFields value={editing.image} alt={editing.image_alt} caption={editing.image_caption} onChange={(image) => setEditing({ ...editing, image })} onMetaChange={(key, value) => setEditing({ ...editing, [key]: value })} />
        <InternalLinkField value={editing.internal_link} onChange={(e) => setEditing({ ...editing, internal_link: e.target.value })} />
        {[["Client Concern", "client_concern"], ["What I Found", "what_i_found"], ["Solution Provided", "solution_provided"], ["Outcome", "outcome"]].map(([label, key]) => <div key={key}><label className="text-xs text-[#94A3B8] mb-1.5 block">{label}</label><Textarea value={editing[key] || ""} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} rows={2} className="bg-white/5 border-white/15 text-white" /></div>)}
      </EditSection>}
      <Section title={`Projects (${items.length})`}>
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="flex items-center gap-4 rounded-xl border border-white/10 p-3">
              <img src={p.image} alt="" className="h-12 w-16 rounded object-cover" />
              <div className="flex-1 min-w-0"><div className="text-white truncate">{p.title}</div><div className="text-xs text-gold">{p.category} · {p.location}</div></div>
              <button onClick={() => setEditing({ ...p, client_concern: p.client_concern || p.challenge || "", what_i_found: p.what_i_found || p.solution || "", solution_provided: p.solution_provided || p.results || "", outcome: p.outcome || "" })} className="text-gold hover:text-white p-2" aria-label={`Edit ${p.title}`}><Pencil size={16} /></button>
              <button onClick={() => del(p.id)} className="text-red-400 hover:text-red-300 p-2" data-testid={`delete-project-${p.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

function TestimonialManager() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ name: "", city: "", photo: "", image_alt: "", image_caption: "", internal_link: "", review: "", rating: 5 });
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/testimonials").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => { e.preventDefault(); if (!f.name || !f.review) return toast.error("Name & review required"); try { await api.post("/testimonials", { ...f, rating: parseInt(f.rating) || 5 }); toast.success("Added"); setF({ name: "", city: "", photo: "", image_alt: "", image_caption: "", internal_link: "", review: "", rating: 5 }); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { await api.delete(`/testimonials/${id}`); toast.success("Deleted"); load(); };
  const update = async (e) => {
    e.preventDefault();
    if (!editing.name || !editing.review) return toast.error("Name & review required");
    try { await api.put(`/testimonials/${editing.id}`, { ...editing, rating: parseInt(editing.rating) || 5 }); toast.success("Testimonial updated"); setEditing(null); load(); } catch { toast.error("Update failed"); }
  };
  return (
    <>
      <Section title="Add Testimonial">
        <form onSubmit={create} className="space-y-4" data-testid="admin-testimonial-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="testimonial-name-input" />
            <Field label="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ImageFields value={f.photo} alt={f.image_alt} caption={f.image_caption} onChange={(photo) => setF({ ...f, photo })} onMetaChange={(key, value) => setF({ ...f, [key]: value })} />
            <Field label="Rating (1-5)" type="number" value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} />
          </div>
          <InternalLinkField value={f.internal_link} onChange={(e) => setF({ ...f, internal_link: e.target.value })} />
          <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Review</label><Textarea value={f.review} onChange={(e) => setF({ ...f, review: e.target.value })} rows={3} className="bg-white/5 border-white/15 text-white" /></div>
          <button className="inline-flex items-center gap-2 rounded-xl bg-primary-green px-5 h-11 text-white font-medium hover:bg-[#059669]"><Plus size={16} /> Add Testimonial</button>
        </form>
      </Section>
      {editing && <EditSection title="Edit Testimonial" onSubmit={update} onCancel={() => setEditing(null)}>
        <div className="grid grid-cols-2 gap-3"><Field label="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /><Field label="City" value={editing.city || ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} /></div>
        <ImageFields value={editing.photo} alt={editing.image_alt} caption={editing.image_caption} onChange={(photo) => setEditing({ ...editing, photo })} onMetaChange={(key, value) => setEditing({ ...editing, [key]: value })} />
        <InternalLinkField value={editing.internal_link} onChange={(e) => setEditing({ ...editing, internal_link: e.target.value })} />
        <Field label="Rating (1-5)" type="number" min="1" max="5" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} />
        <div><label className="text-xs text-[#94A3B8] mb-1.5 block">Review</label><Textarea value={editing.review} onChange={(e) => setEditing({ ...editing, review: e.target.value })} rows={3} className="bg-white/5 border-white/15 text-white" /></div>
      </EditSection>}
      <Section title={`Testimonials (${items.length})`}>
        <div className="space-y-3">
          {items.map((t) => (
            <div key={t.id} className="flex items-center gap-4 rounded-xl border border-white/10 p-3">
              <img src={t.photo} alt="" className="h-10 w-10 rounded-full object-cover" />
              <div className="flex-1 min-w-0"><div className="text-white">{t.name} · <span className="text-gold text-sm">{t.rating}★</span></div><div className="text-xs text-[#94A3B8] truncate">{t.review}</div></div>
              <button onClick={() => setEditing({ ...t })} className="text-gold hover:text-white p-2" aria-label={`Edit ${t.name}`}><Pencil size={16} /></button>
              <button onClick={() => del(t.id)} className="text-red-400 hover:text-red-300 p-2" data-testid={`delete-testimonial-${t.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

function GalleryManager() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", category: "Site Visits", image: "", image_alt: "", image_caption: "", internal_link: "" });
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/gallery").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => { e.preventDefault(); if (!f.title || !f.image) return toast.error("Title & image required"); try { await api.post("/gallery", f); toast.success("Added"); setF({ title: "", category: "Site Visits", image: "", image_alt: "", image_caption: "", internal_link: "" }); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { await api.delete(`/gallery/${id}`); toast.success("Deleted"); load(); };
  const update = async (e) => {
    e.preventDefault();
    if (!editing.title || !editing.image) return toast.error("Title & image required");
    try { await api.put(`/gallery/${editing.id}`, editing); toast.success("Image updated"); setEditing(null); load(); } catch { toast.error("Update failed"); }
  };
  return (
    <>
      <Section title="Add Gallery Image">
        <form onSubmit={create} className="space-y-4" data-testid="admin-gallery-form">
          <Field label="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="gallery-title-input" />
          <Field label="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="e.g. Workshops" />
          <ImageFields value={f.image} alt={f.image_alt} caption={f.image_caption} onChange={(image) => setF({ ...f, image })} onMetaChange={(key, value) => setF({ ...f, [key]: value })} />
          <InternalLinkField value={f.internal_link} onChange={(e) => setF({ ...f, internal_link: e.target.value })} />
          <button className="inline-flex items-center gap-2 rounded-xl bg-primary-green px-5 h-11 text-white font-medium hover:bg-[#059669]"><Plus size={16} /> Add Image</button>
        </form>
      </Section>
      {editing && <EditSection title="Edit Gallery Image" onSubmit={update} onCancel={() => setEditing(null)}>
        <Field label="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
        <Field label="Category" value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
        <ImageFields value={editing.image} alt={editing.image_alt} caption={editing.image_caption} onChange={(image) => setEditing({ ...editing, image })} onMetaChange={(key, value) => setEditing({ ...editing, [key]: value })} />
        <InternalLinkField value={editing.internal_link} onChange={(e) => setEditing({ ...editing, internal_link: e.target.value })} />
      </EditSection>}
      <Section title={`Gallery (${items.length})`}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((g) => (
            <div key={g.id} className="relative group rounded-xl overflow-hidden border border-white/10">
              <img src={g.image} alt={g.title} className="h-28 w-full object-cover" />
              <button onClick={() => setEditing({ ...g })} className="absolute top-2 left-2 bg-black/60 text-gold rounded-full p-1.5 hover:text-white" aria-label={`Edit ${g.title}`}><Pencil size={14} /></button>
              <button onClick={() => del(g.id)} className="absolute top-2 right-2 bg-black/60 text-red-400 rounded-full p-1.5" data-testid={`delete-gallery-${g.id}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

function ContactsViewer() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/contact").then((r) => setItems(r.data)); }, []);
  return (
    <Section title={`Enquiries (${items.length})`}>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-[#94A3B8]">No enquiries yet.</p>}
        {items.map((c) => (
          <div key={c.id} className="rounded-xl border border-white/10 p-4">
            <div className="flex justify-between flex-wrap gap-2">
              <div className="text-white font-medium">{c.full_name} · <span className="text-gold text-sm">{c.service || "General"}</span></div>
              <div className="text-xs text-[#94A3B8]">{new Date(c.created_at).toLocaleString()}</div>
            </div>
            <div className="text-sm text-[#94A3B8] mt-1">{c.phone} · {c.email} · {c.city}</div>
            <p className="text-[#CBD5E1] mt-2">{c.message}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) navigate("/admin");
  }, [user, navigate]);

  if (!user || user === false) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-gold" /></div>;

  return (
    <div className="min-h-screen relative z-10">
      <header className="glass-nav sticky top-0 z-40">
        <div className="mx-auto max-w-6xl px-6 flex items-center justify-between h-[70px]">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center rounded-full bg-white/95 p-1"><img src={BRAND.favicon} alt="ENRICH VASTU" className="h-8 w-8 object-contain rounded-full" /></span>
            <span className="font-display text-xl"><span className="text-gold">ENRICH</span> CMS</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1.5"><ExternalLink size={14} /> View Site</a>
            <button onClick={() => { logout(); navigate("/admin"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-red-400"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-10">
        <h1 className="font-display text-4xl mb-8">Content Management</h1>
        <Tabs defaultValue="blog">
          <TabsList className="bg-white/5 border border-white/10 mb-8 flex-wrap h-auto">
            {["blog", "projects", "testimonials", "gallery", "enquiries"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="capitalize data-[state=active]:bg-primary-green data-[state=active]:text-white">{t}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="blog"><BlogManager /></TabsContent>
          <TabsContent value="projects"><ProjectManager /></TabsContent>
          <TabsContent value="testimonials"><TestimonialManager /></TabsContent>
          <TabsContent value="gallery"><GalleryManager /></TabsContent>
          <TabsContent value="enquiries"><ContactsViewer /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
